import { format } from "lib/globalize-util";
import { defaultCheckError, IRequestAction, IRequestDataAction, progressEpic } from "epics/base-epic";
import { PRODUCT_MEDIA } from "constants/action-types";
import { I360Asset, IAsset, IImageAsset, IVideoAsset } from "constants/assets";
import { IStore } from "reducers";
import { add as snackbarAdd } from "actions/snackbars";
import { ICoreMediaAssets } from "constants/media-asset";
import { Action } from "redux";

export const fetchProductMediaAssets = progressEpic<IRequestAction, ICoreMediaAssets>(
    PRODUCT_MEDIA.MEDIA_ASSETS.FETCH,
    ({ payload }) => ({ method: "GET", url: payload.url }),
    {
        dataCustomizer: (response) => ({ ...response }),
    },
);

// prettier-ignore
export const uploadProductAsset = progressEpic<IRequestDataAction<IVideoAsset | I360Asset | IImageAsset>, IAsset>(
    PRODUCT_MEDIA.UPLOAD,
    ({ payload }) => ({
        method: "POST",
        url: payload.url,
        body: payload.body,
        retry: 1, // we don't need to retry this request in case of failure
    }),
);

// prettier-ignore
export const updateProductAssets = progressEpic<IRequestDataAction<{ asset_ids: string[] }>, IAsset>(
    PRODUCT_MEDIA.MUTATE,
    ({ payload }) => ({ method: "PUT", url: payload.url, body: payload.body }),
    {
        dataCustomizer: (_, __, store: IStore) => store.productMedia.payload,
        finishActions: () => [snackbarAdd({ message: format("Product media updated") })],
    },
);

/**
 * Specifies an arbitrary action, which carries the intermediate error state
 * of the previously processed action, if any.
 */
export interface INextAction extends Action {
    errors?: Array<any>;
}

// prettier-ignore
export const mutateProductMediaAssets = progressEpic<IRequestDataAction & INextAction, ICoreMediaAssets>(
    PRODUCT_MEDIA.MEDIA_ASSETS.MUTATE,
    ({ payload: { url, body } }) => ({ method: "PUT", url, body }),
    {
        dataCustomizer: (response) => ({ media: response.media, revision: response.revision_range.end }),
        finishActions: (_response, { payload: { errors } }) => {
            // We don't use actual error messages of the previously failed/partially completed action bc they might contain non-i18n and/or meaningless strings.
            const hasErrors = errors && errors.length > 0;
            return [
                snackbarAdd({
                    message: hasErrors
                        ? format("Product media updated with issues. Please refresh and try again.")
                        : format("Product media updated"),
                }),
            ];
        },
        checkErrorStatus: ({ xhr }: { xhr: XMLHttpRequest }) => {
            if (xhr.status === 409) {
                throw Error(format("One or more images was already added. Please refresh and try again."));
            } else {
                defaultCheckError(xhr as any);
            }
        },
    },
);

// prettier-ignore
export const fetch360Manifest = progressEpic<IRequestAction, { image_ids: string[] }>(
    PRODUCT_MEDIA.IMAGE_360.MANIFEST,
    ({ payload }) => ({ method: "GET", url: payload.url }),
    {
        dataCustomizer: (response, { payload }) => ({ ...response, url: payload.url }),
        nextPageGetter: null,
    },
);
